/** Typed fetch wrapper. Errors surface the server's machine-readable code. */

export class ApiError extends Error {
  code: string;
  status: number;
  detail: unknown;

  constructor(message: string, code: string, status: number, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.detail = detail;
  }
}

type Options = {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

/**
 * Where the API lives.
 *
 * Same origin by default, which is how the application runs locally and when
 * the server serves the built bundle itself. A deployment that puts the SPA
 * on a CDN sets `VITE_API_BASE` to the API's own origin at build time.
 *
 * `credentials` follows from that: `same-origin` keeps the session cookie off
 * every third-party request by construction, and is only relaxed to `include`
 * when the API is deliberately somewhere else. The two hosts are the same
 * *site*, so the cookie stays SameSite=Lax.
 */
const CONFIGURED = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');
const BASE = CONFIGURED ? `${CONFIGURED}/api` : '/api';
const CREDENTIALS: RequestCredentials = CONFIGURED ? 'include' : 'same-origin';

/** The API prefix, for callers that build a URL themselves
 *  (document and artifact fetches go straight to pdf.js). */
export const API_BASE = BASE;
export const API_CREDENTIALS = CREDENTIALS;

async function parse(response: Response) {
  const type = response.headers.get('content-type') || '';
  if (type.includes('application/json')) return response.json();
  return response.text();
}

export async function request<T = any>(path: string, options: Options = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, signal } = options;
  const init: RequestInit = {
    method,
    credentials: CREDENTIALS,
    headers: { ...headers },
    signal,
  };
  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    init.body = JSON.stringify(body);
    (init.headers as Record<string, string>)['content-type'] = 'application/json';
  }

  const response = await fetch(BASE + path, init);
  if (!response.ok) {
    const payload = await parse(response).catch(() => null);
    const error = (payload && typeof payload === 'object' && (payload as any).error) || {};
    throw new ApiError(
      error.message || `Request failed (${response.status}).`,
      error.code || 'request_failed',
      response.status,
      error.detail,
    );
  }
  return parse(response) as Promise<T>;
}

export const api = {
  get: <T = any>(path: string, options?: Options) => request<T>(path, options),
  post: <T = any>(path: string, body?: unknown, options?: Options) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T = any>(path: string, body?: unknown, options?: Options) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T = any>(path: string, body?: unknown, options?: Options) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  del: <T = any>(path: string, options?: Options) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

/** Fetch binary content (PDFs, exports) and hand back the blob plus headers. */
export async function fetchBlob(path: string, headers: Record<string, string> = {}) {
  const response = await fetch(BASE + path, { credentials: CREDENTIALS, headers });
  if (!response.ok) {
    const payload = await parse(response).catch(() => null);
    const error = (payload && typeof payload === 'object' && (payload as any).error) || {};
    throw new ApiError(error.message || 'Download failed.', error.code || 'download_failed',
      response.status, error.detail);
  }
  return { blob: await response.blob(), headers: response.headers };
}

/** Trigger a browser download from an authenticated endpoint. */
export async function downloadFile(path: string, fallbackName: string) {
  const { blob, headers } = await fetchBlob(path);
  const disposition = headers.get('content-disposition') || '';
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const plain = /filename="([^"]+)"/i.exec(disposition);
  const name = utf8 ? decodeURIComponent(utf8[1]) : plain ? plain[1] : fallbackName;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
